import { useConfiguration } from 'common/hooks/useConfiguration';
import { useInjection } from 'inversify-react';
import { NotificationLevel, ToastNotification } from '../../../../__legacy__/modules/notifications/models/notification.model';
import { INotificationsService } from '../../../../__legacy__/modules/notifications/services/notifications.service';
import { EventListeningDisposer, IEventBus } from '../../../../__legacy__/services/eventBus.service';
import { IProjectHubService } from '../../../../__legacy__/services/projectHub.service';
import { ProjectExportFinishedEvent, ProjectExportFinishedEventType } from './events/ProjectExportFinishedEvent';
import { ProjectExportStartedEvent, ProjectExportStartedEventType } from './events/ProjectExportStartedEvent';
import { IExportDataStore } from './ExportData.store';
import { ExportDataFilter } from './models/ExportDataFilter';
import { IExportProjectService } from './services/ExportProject.service';

export interface IExportDataBl {
  changeOrderAsync(orderBy: string, orderType: string): Promise<void>;
  changePaginationAsync(pageNumber: number, pageSize: number): Promise<void>;
  cleanup(): void;
  exportProjectAsync(): Promise<void>;
  getProjectExportDownloadUrl(projectExportId: string, dataFilterType: ExportDataFilter): Promise<string>;
  hideDownloadModal(): void;
  hideExportModal(): void;
  initializeAsync(projectId: string): Promise<void>;
  isCurrentExportGeneratedAsync(): Promise<boolean>;
  selectDataFilter(newDataFilter: ExportDataFilter): void;
  showDownloadModal(projectExportId: string, selectedFilter: ExportDataFilter): void;
}

export const useExportDataBl = (): IExportDataBl => {
  let projectExportStartedDisposer: EventListeningDisposer | undefined = undefined;
  let projectExportFinishedDisposer: EventListeningDisposer | undefined = undefined;

  const store = useInjection(IExportDataStore.$);
  const notificationService = useInjection(INotificationsService.$);
  const exportProjectService = useInjection(IExportProjectService.$);
  const eventBus = useInjection(IEventBus.$);
  const projectHubService = useInjection(IProjectHubService.$);
  const config = useConfiguration();

  const selectDataFilter = (newDataFilter: ExportDataFilter): void => {
    store.setSelectedDataFilter(newDataFilter);
  };

  const exportProjectAsync = async (): Promise<void> => {
    try {
      store.setIsCurrentlyGenerating(true);
      await exportProjectService.exportProjectAsync({ projectId: store.projectId, dataFilterType: store.selectedDataFilter });
      showExportModal();
    } catch {
      await notificationService.push(new ToastNotification(NotificationLevel.ERROR, 'generating_export_failed'));
    }

    eventBus.sendEvent(new ProjectExportStartedEvent(store.projectId));
  };

  const isCurrentExportGeneratedAsync = async (): Promise<boolean> => {
    try {
      return await exportProjectService.isCurrentExportGeneratedAsync({ projectId: store.projectId });
    } catch (error) {
      await notificationService.push(new ToastNotification(NotificationLevel.ERROR, 'export_failed'));
      throw error;
    }
  };

  const getProjectExportDownloadUrl = async (projectExportId: string, dataFilterType: ExportDataFilter): Promise<string> => {
    const url = await exportProjectService.generateDownloadLinkAsync(projectExportId, dataFilterType);
    return new URL(url, config.appConfig.exportApiBaseUrl).toString();
  };

  const showExportModal = (): void => {
    store.setShowExportModal(true);
  };

  const hideExportModal = (): void => {
    store.setShowExportModal(false);
  };

  const showDownloadModal = (projectExportId: string, selectedFilter: ExportDataFilter): void => {
    store.setShowDownloadModal(true, projectExportId, selectedFilter);
  };

  const hideDownloadModal = (): void => {
    store.setShowDownloadModal(false, '', ExportDataFilter.ALL);
  };

  const loadProjectExportsAsync = async (projectId: string): Promise<void> => {
    try {
      store.setShowLoader(true);
      const response = await exportProjectService.getProjectExportsAsync({ ...store.paging, projectId });
      store.load(projectId, response);
    } catch {
      await notificationService.push(new ToastNotification(NotificationLevel.ERROR, 'getting_project_exports_failed'));
    }
  };

  const handleProjectExportStartedAsync = async (e: ProjectExportStartedEvent): Promise<void> => {
    if (e.projectId === store.projectId) {
      await loadProjectExportsAsync(store.projectId);
    }
  };

  const handleProjectExportFinishedAsync = async (e: ProjectExportFinishedEvent): Promise<void> => {
    if (e.projectId === store.projectId) {
      await loadProjectExportsAsync(store.projectId);
    }
  };

  const initializeAsync = async (projectId: string): Promise<void> => {
    projectExportStartedDisposer = eventBus.addListener(handleProjectExportStartedAsync, ProjectExportStartedEventType);
    projectExportFinishedDisposer = eventBus.addListener(handleProjectExportFinishedAsync, ProjectExportFinishedEventType);

    await projectHubService.initializeAsync();
    await loadProjectExportsAsync(projectId);
  };

  const cleanup = (): void => {
    projectExportStartedDisposer?.();
    projectExportFinishedDisposer?.();
  };

  const changePaginationAsync = async (pageNumber: number, pageSize: number): Promise<void> => {
    store.paging.pageNumber = pageNumber;
    store.paging.pageSize = pageSize;

    await loadProjectExportsAsync(store.projectId);
  };

  const changeOrderAsync = async (orderBy: string, orderType: string): Promise<void> => {
    store.paging.orderBy = orderBy || '';
    store.paging.orderType = orderType || '';

    await loadProjectExportsAsync(store.projectId);
  };

  return {
    selectDataFilter,
    exportProjectAsync,
    getProjectExportDownloadUrl,
    hideDownloadModal,
    hideExportModal,
    initializeAsync,
    isCurrentExportGeneratedAsync,
    changePaginationAsync,
    changeOrderAsync,
    cleanup,
    showDownloadModal,
  };
};
